import SEOMetadata from '@/components/SEOMetadata';
import React, { useRef, useEffect, useState, Suspense } from 'react';
import StyledProducts from './styles/ProductsPageStyles';
import Footer from '@/components/landing/Footer';
import GetStarted from '@/components/landing/GetStarted';
import PricingTable from '@/components/landing/PricingTable'
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';

import Quad from '@/components/landing/product/main/Quad'

const loadLogos = () => import('@/components/landing/product/sub/Logos')
const Logos = React.lazy(loadLogos)
const loadAPIGrid = () => import('@/components/landing/product/main/APIGrid')
const APIGrid = React.lazy(loadAPIGrid)
const loadDatasetMason = () => import('@/components/landing/product/main/DatasetMason')
const DatasetMason = React.lazy(loadDatasetMason)
const loadSDKGrid = () => import('@/components/landing/product/main/SDKGrid')
const SDKGrid = React.lazy(loadSDKGrid)
const loadDatasetDetails = () => import('@/components/landing/product/main/DatasetDetails')
const DatasetDetails = React.lazy(loadDatasetDetails)
const loadIntegrationSection = () => import('@/components/landing/product/main/IntegrationSection')
const IntegrationSection = React.lazy(loadIntegrationSection)

const loadIndicesChart = () => import('@/components/landing/product/main/datasets/indices');
const IndicesChart = React.lazy(loadIndicesChart);
const loadETFChart = () => import('@/components/landing/product/main/datasets/etf-performance-card');
const ETFChart = React.lazy(loadETFChart);
const loadMarketPulse = () => import('@/components/landing/product/main/datasets/stock-market-pulse-card');
const MarketPulse = React.lazy(loadMarketPulse);
const loadChat = () => import('@/components/landing/product/main/datasets/chat-bubble');
const Chat = React.lazy(loadChat);

const loadFins = () => import('@/components/landing/product/main/datasets/financials');
const Fins = React.lazy(loadFins);
const loadFilings = () => import('@/components/landing/product/main/datasets/filings');
const Filings = React.lazy(loadFilings);
const loadMacro = () => import('@/components/landing/product/main/datasets/macro-regime-card');
const Macro = React.lazy(loadMacro);
const loadCatRadar = () => import('@/components/landing/product/main/datasets/catalyst-radar-card');
const CatRadar = React.lazy(loadCatRadar);

const loadMap = () => import('@/components/landing/product/main/datasets/ratings-pressure-card');
const Map = React.lazy(loadMap);
const loadMap2 = () => import('@/components/landing/product/main/datasets/sentiment');
const Map2 = React.lazy(loadMap2);
const loadSC = () => import('@/components/landing/product/main/datasets/corporate-dependency-map-card');
const SC = React.lazy(loadSC);
const loadTraffic = () => import('@/components/landing/product/main/datasets/traffic-funnel-card');
const Traffic = React.lazy(loadTraffic);
const loadAttention = () => import('@/components/landing/product/main/datasets/consumer-attention-card');
const Attention = React.lazy(loadAttention);


const ProductsPage: React.FC = () => {
  useIdlePrefetch([loadLogos, loadAPIGrid, loadDatasetMason, loadSDKGrid, loadDatasetDetails, loadIntegrationSection, loadIndicesChart, loadETFChart, loadMarketPulse, loadChat, loadFins, loadFilings, loadMacro, loadCatRadar, loadMap, loadMap2, loadSC, loadTraffic, loadAttention]);
  const graphicRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => { window.track('product_page_visited') }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        root: null,
        rootMargin: '200px',
        threshold: 0.01
      }
    );

    if (graphicRef.current) {
      observer.observe(graphicRef.current);
    }

    return () => {
      if (graphicRef.current) {
        observer.unobserve(graphicRef.current);
      }
    };
  }, []);


  return (
    <>
      <SEOMetadata
        title="Financial Data API Products & Coverage | AxionQuant"
        description="Explore AxionQuant's financial data API products: stocks, ETFs, forex, crypto, fundamentals, sentiment, ESG, and alternative data. Free tier available."
        canonicalUrl="https://axionquant.com/products"
      />
      <StyledProducts>

        <div ref={graphicRef}>
          <Quad  isVisible={isVisible}/>
        </div>

        <Logos/>

        <APIGrid/>

        <DatasetMason/>

        <SDKGrid/>

        <Suspense fallback={<div style={{height: 400}}/>}>
          <DatasetDetails
            title="Market data that scales across global markets"
            subtitle="Access institutional-grade market data across global asset classes through a single, consistent financial data API. From daily and hourly stock prices and decades of historical market data to exchange metadata and security profiles, build research applications without stitching together multiple providers."
            cta="Explore Market Data"
            ctaLink="/products/stocks"
            tiles={[
              {
                img: '7.webp',
                title: 'Global Coverage',
                caption:
                'Stocks, ETFs, forex, futures, indices, and crypto from exchanges around the world, including US and global stock market data in one feed.',
                cn: 'scaled',
                tile: <IndicesChart/>
              },
              {
                img: '13.webp',
                title: 'Historical OHLCV',
                caption:
                'Power backtests with decades of historical OHLCV data back to 1987, retrievable nearly instantly.',
                cn: 'scaled',
                tile: <ETFChart/>
              },
              {
                img: '2.webp',
                title: 'Rich Security Metadata',
                caption:
                'Exchange information, trading currencies, company profiles, market details, and instrument identifiers.',
                cn: 'scaled',
                tile: <MarketPulse/>
              },
              {
                img: '1.webp',
                title: 'Built for Developers, Ready for LLMs',
                caption:
                'Consistent schemas, predictable APIs, and SDKs designed for quantitative research and production systems.',
                cn: '',
                tile: <Chat/>
              }
            ]}
          />
        </Suspense>


        <Suspense fallback={<div style={{height: 400}}/>}>
          <DatasetDetails
            title="Fundamental data without the fragmentation"
            subtitle="Research companies, evaluate financial performance, and monitor market-moving events through a unified fundamental data API. Access company fundamentals, financial statements, earnings, filings, macroeconomic indicators, and news with consistent schemas designed for quantitative research and long-term investing."
            cta="Explore Fundamentals"
            ctaLink="/products/corporate-profiles"
            tiles={[
              {
                img: '0.webp',
                title: 'Financial Statements',
                caption:
                'Income statements, balance sheets, cash flow statements, ratios, and standardized financial metrics.',
                cn: 'scaled',
                tile: <Fins/>
              },
              {
                img: '14.webp',
                title: 'Corporate Events',
                caption:
                'Earnings releases, analyst estimates, SEC filings, corporate disclosures, and key company events.',
                cn: 'scaled',
                tile: <Filings/>
              },
              {
                img: '5.webp',
                title: 'Economic Intelligence',
                caption:
                'Macroeconomic indicators, economic calendars, and datasets that provide market context.',
                cn: 'scaled',
                tile: <Macro/>
              },
              {
                img: '12.webp',
                title: 'Research Context',
                caption:
                'Company profiles, executive information, industry classification, and news organized by company and sector.',
                cn: 'scaled',
                tile: <CatRadar/>
              }
            ]}
          />
        </Suspense>


        <Suspense fallback={<div style={{height: 400}}/>}>
          <DatasetDetails
            title="Alternative data API for differentiated research"
            subtitle="Go beyond prices and financial statements with an alternative data API that uncovers competitive positioning, market sentiment, consumer interest, and operational relationships. Discover signals that traditional market data often misses."
            cta="Explore Alternative Data"
            ctaLink="/products/esg"
            tiles={[
              {
                img: '3.webp',
                title: 'Market Signals',
                caption:
                'Aggregated sentiment, ESG metrics, and credit ratings to monitor market perception and risk.',
                cn: 'scaled overthetop',
                tile: (<div>
                  <Map/>
                  <div style={{height: '20px'}}/>
                  <Map2/>
                </div>)
              },
              {
                img: '9.webp',
                title: 'Business Relationships',
                caption:
                'Supply chain networks, customers, suppliers, competitors, and peer relationships.',
                cn: 'scaled corner',
                tile: <SC/>
              },
              {
                img: '6.webp',
                title: 'Digital Footprint',
                caption:
                'Website traffic, engagement trends, and online visibility to measure consumer and business activity.',
                cn: 'scaled',
                tile: <Traffic/>
              },
              {
                img: '10.webp',
                title: 'Unique Investment Signals',
                caption:
                'Combine alternative datasets with market and fundamental data to build richer models and uncover differentiated alpha.',
                cn: 'scaled',
                tile: <Attention/>
              }
            ]}
          />
        </Suspense>
        <IntegrationSection/>

        <PricingTable />
        <GetStarted />
        <Footer />
      </StyledProducts>
    </>
  );
};

export default ProductsPage;

